import { Suspense, useState } from "react"
import { DashboardShell } from "@/components/dashboard-shell"
import { Breadcrumb, BreadcrumbItem, BreadcrumbList, BreadcrumbPage } from "@/components/ui/breadcrumb"
import { Separator } from "@/components/ui/separator"
import { SidebarTrigger } from "@/components/ui/sidebar"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Search, BookOpen, FileText, Code, Lightbulb, Shield, ChevronRight } from "lucide-react"

const docCategories = [
  {
    id: "getting-started",
    title: "Getting Started",
    icon: Lightbulb,
    description: "Quick start guides and platform overview",
    articles: [
      { id: 1, title: "Platform Overview", readTime: "5 min" },
      { id: 2, title: "Quick Start Guide", readTime: "10 min" },
      { id: 3, title: "Understanding Gate 2 Scores", readTime: "8 min" },
      { id: 4, title: "Navigation & Interface", readTime: "6 min" },
    ],
  },
  {
    id: "projects",
    title: "Projects",
    icon: FileText,
    description: "Managing and analyzing renewable energy projects",
    articles: [
      { id: 5, title: "Project Dashboard Overview", readTime: "7 min" },
      { id: 6, title: "Adding Projects to Watchlist", readTime: "4 min" },
      { id: 7, title: "Understanding Project Metrics", readTime: "12 min" },
      { id: 8, title: "Filtering and Search", readTime: "5 min" },
    ],
  },
  {
    id: "analytics",
    title: "Analytics & Reports",
    icon: BookOpen,
    description: "Data analysis and reporting features",
    articles: [
      { id: 9, title: "Gate 2 Analytics Dashboard", readTime: "10 min" },
      { id: 10, title: "Regional Analysis Tools", readTime: "8 min" },
      { id: 11, title: "Technology Trends", readTime: "9 min" },
      { id: 12, title: "Custom Reports", readTime: "15 min" },
    ],
  },
  {
    id: "api",
    title: "API Reference",
    icon: Code,
    description: "Developer documentation and API guides",
    articles: [
      { id: 13, title: "Authentication", readTime: "6 min" },
      { id: 14, title: "Projects API", readTime: "12 min" },
      { id: 15, title: "Analytics API", readTime: "10 min" },
      { id: 16, title: "Rate Limits & Best Practices", readTime: "7 min" },
    ],
  },
  {
    id: "security",
    title: "Security & Compliance",
    icon: Shield,
    description: "Data security and compliance information",
    articles: [
      { id: 17, title: "Data Privacy Policy", readTime: "8 min" },
      { id: 18, title: "Access Control", readTime: "6 min" },
      { id: 19, title: "Multi-Tenancy Security", readTime: "10 min" },
      { id: 20, title: "Compliance Certifications", readTime: "5 min" },
    ],
  },
]

const popularArticles = [
  { id: 3, title: "Understanding Gate 2 Scores", category: "Getting Started", views: 2340 },
  { id: 7, title: "Understanding Project Metrics", category: "Projects", views: 1890 },
  { id: 9, title: "Gate 2 Analytics Dashboard", category: "Analytics & Reports", views: 1650 },
  { id: 12, title: "Custom Reports", category: "Analytics & Reports", views: 1420 },
]

function DocsContent() {
  const [searchQuery, setSearchQuery] = useState("")
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)

  return (
    <>
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Documentation</h1>
        <p className="text-muted-foreground">Learn how to use Kanu Energy platform effectively</p>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search documentation..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-4">
          <h2 className="text-xl font-semibold">Browse by Category</h2>
          <div className="grid gap-4">
            {docCategories.map((category) => {
              const Icon = category.icon
              return (
                <Card
                  key={category.id}
                  className="cursor-pointer transition-all hover:border-primary/50 hover:shadow-md"
                  onClick={() => setSelectedCategory(category.id)}
                >
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-primary/10">
                          <Icon className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <CardTitle className="text-lg">{category.title}</CardTitle>
                          <CardDescription className="mt-1">{category.description}</CardDescription>
                        </div>
                      </div>
                      <Badge variant="secondary">{category.articles.length} articles</Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="h-32">
                      <div className="space-y-2">
                        {category.articles.map((article) => (
                          <div
                            key={article.id}
                            className="flex items-center justify-between p-2 rounded hover:bg-muted/50 cursor-pointer"
                          >
                            <div className="flex items-center gap-2">
                              <ChevronRight className="h-4 w-4 text-muted-foreground" />
                              <span className="text-sm">{article.title}</span>
                            </div>
                            <span className="text-xs text-muted-foreground">{article.readTime}</span>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  </CardContent>
                </Card>
              )
            })}
          </div>
        </div>

        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Popular Articles</CardTitle>
              <CardDescription>Most viewed documentation</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {popularArticles.map((article, idx) => (
                  <div key={article.id} className="space-y-2">
                    <div className="flex items-start gap-3">
                      <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold">
                        {idx + 1}
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-sm hover:text-primary cursor-pointer">{article.title}</div>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className="text-xs">
                            {article.category}
                          </Badge>
                          <span className="text-xs text-muted-foreground">{article.views} views</span>
                        </div>
                      </div>
                    </div>
                    {idx < popularArticles.length - 1 && <Separator />}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Quick Links</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex items-center gap-2 p-2 rounded hover:bg-muted/50 cursor-pointer">
                <FileText className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">Release Notes</span>
              </div>
              <div className="flex items-center gap-2 p-2 rounded hover:bg-muted/50 cursor-pointer">
                <Code className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">API Status</span>
              </div>
              <div className="flex items-center gap-2 p-2 rounded hover:bg-muted/50 cursor-pointer">
                <Lightbulb className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">Feature Requests</span>
              </div>
              <div className="flex items-center gap-2 p-2 rounded hover:bg-muted/50 cursor-pointer">
                <BookOpen className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm">Video Tutorials</span>
              </div>
            </CardContent>
          </Card>

          <Card className="border-primary/20 bg-primary/5">
            <CardHeader>
              <CardTitle className="text-lg">Need Help?</CardTitle>
              <CardDescription>Contact our support team</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div>
                <span className="text-muted-foreground">Email:</span>
                <div className="font-medium">support@kanuenergy.com</div>
              </div>
              <div>
                <span className="text-muted-foreground">Response time:</span>
                <div className="font-medium">Within 24 hours</div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  )
}

export default function DocumentationPage() {
  return (
    <DashboardShell>
        <header className="flex h-16 shrink-0 items-center gap-2 border-b border-border/50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <div className="flex items-center gap-2 px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbPage>Documentation</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </div>
        </header>

        <div className="flex flex-1 flex-col gap-6 p-6">
          <Suspense fallback={null}>
            <DocsContent />
          </Suspense>
        </div>
      </DashboardShell>
  )
}
